import { motion } from 'framer-motion';
import { cn } from '@shared/lib/cn';

/**
 * Step indicator for multi-step flows (onboarding `flow_template` runner).
 * Dots + connecting line, current = filled primary, future = hairline outline.
 *
 * Compact: rendered above forms in modal sheets and full-page onboarding.
 */
interface StepperProps {
  total: number;
  current: number;
  /** Optional labels rendered under each dot. Pass empty array to omit. */
  labels?: string[];
  className?: string;
  onStepSelect?: (index: number) => void;
  isStepAccessible?: (index: number) => boolean;
}

export function Stepper({
  total,
  current,
  labels,
  className,
  onStepSelect,
  isStepAccessible,
}: StepperProps): JSX.Element {
  const safeTotal = Math.max(total, 1);
  const safeCurrent = Math.min(Math.max(current, 0), safeTotal - 1);
  const hasTrack = safeTotal > 1;
  const trackInset = 100 / (safeTotal * 2);
  const progressPercent = hasTrack ? (safeCurrent / (safeTotal - 1)) * 100 : 0;
  const canSelectStep = (index: number) => Boolean(onStepSelect && isStepAccessible?.(index));

  return (
    <div
      className={cn('flex flex-col gap-2', className)}
      role="progressbar"
      aria-valuenow={safeCurrent + 1}
      aria-valuemin={1}
      aria-valuemax={safeTotal}
    >
      <div className="relative">
        {hasTrack ? (
          <div
            className="absolute top-[0.3125rem] h-px bg-hairline"
            style={{ left: `${trackInset}%`, right: `${trackInset}%` }}
          >
            <motion.div
              initial={false}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
              className="h-full bg-primary-600"
            />
          </div>
        ) : null}

        <div
          className="grid gap-y-2"
          style={{ gridTemplateColumns: `repeat(${safeTotal}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: safeTotal }).map((_, i) => {
            const filled = i <= safeCurrent;
            const clickable = canSelectStep(i);
            const label = labels && labels.length === safeTotal ? labels[i] : `Step ${i + 1}`;

            return (
              <div key={i} className="flex min-w-0 flex-col items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (clickable) onStepSelect?.(i);
                  }}
                  disabled={!clickable}
                  aria-current={i === safeCurrent ? 'step' : undefined}
                  aria-label={label}
                  className={cn(
                    'flex min-w-0 flex-col items-center gap-2 rounded-sm px-1 transition-opacity duration-fast',
                    clickable ? 'cursor-pointer' : 'cursor-default',
                    !clickable && 'disabled:opacity-100',
                  )}
                >
                  <motion.div
                    animate={{ scale: i === safeCurrent ? 1.0 : 0.85 }}
                    transition={{ duration: 0.2 }}
                    className={cn(
                      'relative z-10 h-2.5 w-2.5 rounded-pill border bg-paper transition-colors duration-fast',
                      filled ? 'border-primary-600 bg-primary-600' : 'border-hairline-strong bg-paper',
                      clickable && 'hover:border-primary-500',
                    )}
                  />

                  {labels && labels.length === safeTotal ? (
                    <span
                      className={cn(
                        'min-w-0 text-center text-caption text-ink-muted transition-colors duration-fast',
                        i === safeCurrent && 'font-semibold text-ink-secondary',
                        clickable && i !== safeCurrent && 'hover:text-ink-secondary',
                      )}
                    >
                      {labels[i]}
                    </span>
                  ) : null}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
